import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calculator } from 'lucide-react';
import { useCalculator } from '../context/CalculatorContext';
import { motion } from 'framer-motion';
import LongTermCentralData from './longTermCalculator/LongTermCentralData';
import LongTermSpecificData from './longTermCalculator/LongTermSpecificData';
import LongTermResults from './longTermCalculator/LongTermResults';

interface LongTermCalculatorProps {
  onBack: () => void;
}

const LongTermCalculator: React.FC<LongTermCalculatorProps> = ({ onBack }) => {
  const { updateInvestment } = useCalculator();
  const [activeStep, setActiveStep] = useState<'centralData' | 'specificData' | 'results'>('centralData');

  // Set fixed parameters for long-term calculator
  useEffect(() => {
    updateInvestment({
      isLongTermCalculator: true,
      firstYearUtilityToUser: true,
      reinvestProfits: true,
      additionalContributions: false,
      lemonPriceIncrease: 4 // Set to 4% specifically for this calculator
    });
  }, []); // Remove updateInvestment from dependencies to prevent infinite loop

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  const handleNextStep = () => {
    switch (activeStep) {
      case 'centralData':
        setActiveStep('specificData');
        break;
      case 'specificData':
        setActiveStep('results');
        break;
      default:
        break;
    }
  };

  const handlePreviousStep = () => {
    switch (activeStep) {
      case 'specificData':
        setActiveStep('centralData');
        break;
      case 'results':
        setActiveStep('specificData');
        break;
      default:
        break;
    }
  };

  const handleReset = () => {
    setActiveStep('centralData');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 'centralData':
        return <LongTermCentralData onNext={handleNextStep} />;
      case 'specificData':
        return <LongTermSpecificData onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      case 'results':
        return <LongTermResults onNext={handleNextStep} onPrevious={handlePreviousStep} onReset={handleReset} />;
      default:
        return <LongTermCentralData onNext={handleNextStep} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-6">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100/50 rounded-xl transition-all duration-200"
              title="Volver al menú principal"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Volver</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Certificado Sin Enganche – Acceso Inmediato</h1>
                <p className="text-gray-600">Inversión con mayor horizonte y beneficios especiales</p>
              
              {/* Disclaimer */}
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>⚠️ Aviso:</strong> Las proyecciones mostradas son estimaciones y no garantizan rendimientos futuros.
                </p>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Step 1 - Datos Centrales */}
              <div className={`flex flex-col items-center ${activeStep === 'centralData' ? 'text-amber-600' : activeStep !== 'centralData' ? 'text-amber-400' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep === 'centralData' ? 'bg-amber-100 text-amber-600 ring-2 ring-amber-500' : 
                  activeStep !== 'centralData' ? 'bg-amber-50 text-amber-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">1</span>
                </div>
                <span className="text-xs mt-1">Datos Centrales</span>
              </div>
              
              {/* Connector Line 1-2 */}
              <div className={`h-0.5 w-10 mx-2 ${activeStep !== 'centralData' ? 'bg-amber-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 2 - Datos Específicos */}
              <div className={`flex flex-col items-center ${activeStep === 'specificData' ? 'text-amber-600' : activeStep === 'results' ? 'text-amber-400' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep === 'specificData' ? 'bg-amber-100 text-amber-600 ring-2 ring-amber-500' : 
                  activeStep === 'results' ? 'bg-amber-50 text-amber-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">2</span>
                </div>
                <span className="text-xs mt-1">Datos Específicos</span>
              </div>
              
              {/* Connector Line 2-3 */}
              <div className={`h-0.5 w-10 mx-2 ${activeStep === 'results' ? 'bg-amber-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 3 - Resultados */}
              <div className={`flex flex-col items-center ${activeStep === 'results' ? 'text-amber-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep === 'results' ? 'bg-amber-100 text-amber-600 ring-2 ring-amber-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">3</span>
                </div>
                <span className="text-xs mt-1">Resultados</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <motion.div 
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          {renderStepContent()}
        </motion.div>
      </div>
    </div>
  );
};

export default LongTermCalculator;